/* eslint-disable @typescript-eslint/no-explicit-any */
import { StyledSearchInput } from "@aureskonnect/react-ui";
import { t } from "i18next";
import styled from "styled-components";
import { useSnapshot } from "valtio";

import React from "react";
import BTable from "react-bootstrap/Table";
import { useGlobalFilter, usePagination, useTable } from "react-table";

import { SpinnerWrapper } from "@components/common/SpinnerWrapper";
import { KeyboardWrapper } from "@components/Synchronization/KeyboardWrapper";

import {
    setKeyboardInputsValues,
    kioskStore as store,
} from "@pages/Kiosk/store";

import NodataFound from "./NodataFound";
import { Pagination } from "./Pagination";

type PropsType = {
    columns: TableColumnsType[];
    data: EntityType[];
    isLoading: boolean;
};

const Styles = styled.div`
    table {
        thead {
            font: normal bold 22px/55px Bebas Neue;
            letter-spacing: 0.88px;
            color: #0a0a0a;
            text-align: center;
        }

        tbody td {
            text-align: center;
            font: normal 12px/28px Arial;
            letter-spacing: 0px;
            vertical-align: middle;
        }
        tbody td:nth-child(4) {
            width: 180px;
        }
    }
`;

export function Table({ columns, data, isLoading }: PropsType): JSX.Element {
    const { keyboardInputsValues } = useSnapshot(store);
    const [isKeyBoardVisible, setIsKeyBoardVisible] = React.useState(false);
    const [itemPerPage, setItemPerPage] = React.useState(10);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex },
        setGlobalFilter,
    } = useTable(
        {
            columns,
            data,
            initialState: { pageIndex: 0 },
        },
        useGlobalFilter,
        usePagination
    );

    React.useEffect(() => {
        setGlobalFilter(Object.values(keyboardInputsValues)[0]);
    }, [keyboardInputsValues]);

    return (
        <React.Fragment>
            <StyledSearchInput
                onFocus={() => {
                    setIsKeyBoardVisible(true);
                    setPageSize(5);
                    setItemPerPage(5);
                }}
                onBlur={() => {
                    setPageSize(10);
                    setItemPerPage(10);
                }}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const inputs = event.target.value;
                    setKeyboardInputsValues({ inputs });
                }}
                placeholder={`${t("Search")} ...`}
                value={Object.values(keyboardInputsValues)[0]}
            />
            <Styles>
                <BTable
                    responsive
                    {...getTableProps()}
                    style={{
                        display: "block",
                    }}
                    onClick={() => {
                        setIsKeyBoardVisible(false);
                    }}
                >
                    <thead
                        className="d-flex w-100"
                        style={{
                            position: "sticky",
                            top: "0px",
                            background:
                                "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                            borderBottom: "2px solid #e2e8f0",
                            zIndex: 10,
                        }}
                    >
                        {headerGroups.map((headerGroup: any) => {
                            const { key: headerGroupKey } =
                                headerGroup.getHeaderGroupProps();
                            return (
                                <tr
                                    className="d-flex justify-content-between"
                                    key={headerGroupKey}
                                    {...headerGroup.getHeaderGroupProps()}
                                    style={{
                                        width: "100%",
                                    }}
                                >
                                    {headerGroup.headers.map(
                                        (column: any, index: number) => {
                                            const { key: headerKey } =
                                                column.getHeaderProps();
                                            return (
                                                <th
                                                    key={headerKey}
                                                    {...column.getHeaderProps()}
                                                    style={{
                                                        flex: 1,
                                                        padding: "1rem",
                                                        borderBottom: "none",
                                                        borderRight:
                                                            index <
                                                            headerGroup.headers
                                                                .length -
                                                                1
                                                                ? "1px solid #e2e8f0"
                                                                : "none",
                                                        fontSize: "14px",
                                                        fontWeight: "600",
                                                        color: "#374151",
                                                        textTransform:
                                                            "uppercase",
                                                        letterSpacing: "0.5px",
                                                        background:
                                                            "transparent",
                                                    }}
                                                >
                                                    {column.render("Header")}
                                                </th>
                                            );
                                        }
                                    )}
                                </tr>
                            );
                        })}
                    </thead>
                    <tbody
                        className="d-flex flex-column w-100"
                        style={{
                            overflowY: "scroll",
                            height: isKeyBoardVisible ? "40vw" : "80vw",
                            scrollbarWidth: "none",
                            background:
                                "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                        }}
                        {...getTableBodyProps()}
                    >
                        {isLoading ? (
                            <SpinnerWrapper />
                        ) : page.length ? (
                            // eslint-disable-next-line
                            page.map((row: any) => {
                                prepareRow(row);
                                const { key: rowKey } = row.getRowProps();

                                return (
                                    <tr
                                        className="d-flex justify-content-between"
                                        key={rowKey}
                                        {...row.getRowProps()}
                                        style={{
                                            width: "100%",
                                        }}
                                    >
                                        {/* eslint-disable-next-line */}
                                        {row.cells.map((cell: any) => {
                                            const { key: cellKey } =
                                                cell.getCellProps();
                                            return (
                                                <td
                                                    className="white-space-nowrap three-lines-preview-text"
                                                    key={cellKey}
                                                    {...cell.getCellProps()}
                                                    style={{ width: "180px" }}
                                                >
                                                    {cell.render("Cell")}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })
                        ) : (
                            <NodataFound
                                colSpanLength={headerGroups[0]?.headers?.length}
                            />
                        )}
                    </tbody>
                </BTable>
            </Styles>
            {data && data.length > itemPerPage ? (
                <Pagination
                    canPreviousPage={canPreviousPage}
                    canNextPage={canNextPage}
                    pageOptions={pageOptions}
                    pageCount={pageCount}
                    gotoPage={gotoPage}
                    nextPage={nextPage}
                    previousPage={previousPage}
                    pageIndex={pageIndex}
                />
            ) : null}
            {isKeyBoardVisible ? <KeyboardWrapper /> : null}
        </React.Fragment>
    );
}
